import type { AssistantContextItem } from "@/lib/use-assistant-context";
import { visibleUserText } from "@/stores/agent-store";

// The Studio note's own parts, which the GROWTH assistant writes when the person saves a Grow improve
// answer (spark#1218, "Die eigenen Teile des Notizbuchs entstehen beim Speichern"; agent-service#133,
// "The note's own parts, written at save"). One request carries the improve answer's compact examples and
// the context its click froze; the answer comes back as tagged blocks, which spark splits and files into
// the note it has already created.

/**
 * The blocks of the note request's answer, in the order it writes them. The same names as agent-service's
 * GROWTH note fragment and its GrowthNoteCheck.BLOCKS, the other side of this contract.
 */
export const GROW_NOTE_BLOCKS = ["reference-points", "growth-pattern", "short-term", "long-term", "start", "sources"] as const;
type GrowNoteBlock = (typeof GROW_NOTE_BLOCKS)[number];

/** How many examples a saved note holds, each with its one goal: one to three, chosen by index (spark#1218). */
export type GrowNoteCount = 1 | 2 | 3;

/** The examples an improve answer has, and so the most a note can hold: the chip saves all of them. */
export const GROW_NOTE_MAX_ITEMS: GrowNoteCount = 3;

/** What the save sends besides the answer, frozen at the improve click (spark#1218). */
export interface GrowNoteRequest {
  /**
   * The question in the app's language, by the number of examples the note holds. It names the power and
   * nothing personal.
   */
  briefs: Readonly<Record<GrowNoteCount, string>>;
  /** The items the improve click registered, unchanged, then the note's fixed texts. */
  context: AssistantContextItem[];
}

/** The parts the note request wrote, cleaned for the note. */
export interface GrowNoteParts {
  referencePoints: string | null;
  growthPattern: string | null;
  shortTerm: string;
  longTerm: string;
  /**
   * The one sentence on which of the examples to try first and why it is the easy way in: the note's
   * summary (spark#1218, "Reihenfolge im Notizbuch"). Null when the answer has none.
   */
  start: string | null;
  /** The titles of the reference works the request used, without pages; empty when it used none. */
  sources: string[];
}

// The lines around the improve answer in the message, agent-service's own data-block form.
const ANSWER_OPEN = "<<<IMPROVE_ANSWER";
const ANSWER_CLOSE = ">>>";

/**
 * The one user message of a note request: the brief, then the improve answer's examples as data. A `>>>`
 * inside them is broken up, so it cannot close the block early.
 */
export function growNoteMessage(brief: string, examples: string): string {
  return `${brief}\n\n${ANSWER_OPEN}\n${examples.replace(/>>>/g, "> > >")}\n${ANSWER_CLOSE}`;
}

/** The longest wish text a note request carries, in code points; the BFF and agent-service hold the same cap. */
export const GROW_NOTE_WISHES_MAX = 500;

/**
 * The person's wishes for a note they asked for in their own words: the message they typed in that turn, as
 * they see it in their bubble, trimmed and cut to `GROW_NOTE_WISHES_MAX` code points. Never text the model
 * wrote and never a hidden brief, so nothing a reference passage or a profile says can become a wish
 * (spark#1218).
 */
export function growNoteWishes(typed: string): string {
  const visible = visibleUserText(typed).trim();
  return Array.from(visible).slice(0, GROW_NOTE_WISHES_MAX).join("").trimEnd();
}

/**
 * The examples a typed save asked for, from the `SaveGrowNote` tool's `examples` argument: a non-empty list
 * of distinct whole numbers from 1 to 3, in any order. Sorted ascending; null for anything else, so the model
 * cannot hand over an example that is not there (spark#1218).
 */
export function growNoteSelection(value: unknown): GrowNoteCount[] | null {
  if (!Array.isArray(value) || value.length === 0 || value.length > GROW_NOTE_MAX_ITEMS) return null;
  const chosen = value.filter(
    (index): index is GrowNoteCount => Number.isInteger(index) && index >= 1 && index <= GROW_NOTE_MAX_ITEMS,
  );
  if (chosen.length !== value.length || new Set(chosen).size !== chosen.length) return null;
  return [...chosen].sort((a, b) => a - b);
}

const tagLine = (name: GrowNoteBlock, closing: boolean) =>
  new RegExp(`^[ \\t]*<${closing ? "\\/" : ""}${name}>[ \\t]*$`, "i");
const ANY_TAG = new RegExp(`</?(?:${GROW_NOTE_BLOCKS.join("|")})>`, "gi");

// Figure markers, as agent-service's FigureRenderer finds them: wrapped in the model's own image or link
// syntax, in backticks, or bare. The note has no figure to show, so all three go.
// Each on one line, so a stray "[[img:" cannot swallow the lines up to a later "]]".
const WRAPPED_MARKER = /!?\[[^\]\r\n]*\]\(\s*\[\[img:[^\]\r\n]+\]\]\s*\)/g;
const BACKTICK_MARKER = /`\s*\[\[img:[^\]\r\n]+\]\]\s*`/g;
const MARKER = /\[\[img:[^\]\r\n]+\]\]/g;

function withoutMarkers(text: string): string {
  return text.replace(WRAPPED_MARKER, "").replace(BACKTICK_MARKER, "").replace(MARKER, "");
}

/** A block's text as the note keeps it: no marker, no tag, every heading at the level the note's examples use. */
function clean(text: string): string {
  return withoutMarkers(text)
    .replace(ANY_TAG, "")
    .replace(/^[ \t]{0,3}#{1,6}(?=[ \t])/gm, "###")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// A page in any form a source line could carry one: "S. 136 f.", "p. 12", ", Seite 4", or the search
// result's own "<document>:<page>". The note names a work by its title only (spark#1218).
const PAGE_SUFFIXES = [
  /\s*[,;(]\s*(?:S\.|Seiten?|pp?\.|pages?)\s*\d.*$/i,
  /\s+(?:S\.|pp?\.)\s*\d.*$/i,
  /:\s*\d+(?:\s*[-–]\s*\d+)?\)?$/,
];

function sourceTitles(text: string): string[] {
  const titles = clean(text)
    .split("\n")
    .map((line) => {
      let title = line.replace(/^\s*(?:[-*+•]|\d+[.)])\s+/, "");
      for (const page of PAGE_SUFFIXES) title = title.replace(page, "");
      return title
        .trim()
        .replace(/^(\*\*|__|\*|_)(.+)\1$/, "$2")
        .replace(/^["„“”'‚‘’«»](.+?)["“”'‘’«»]$/, "$1")
        .trim();
    })
    .filter(Boolean);
  return [...new Set(titles)];
}

/** The heading lines in a block, at any level, as `clean` finds them. */
function headingCount(text: string): number {
  return text.split("\n").filter((line) => /^[ \t]{0,3}#{1,6}[ \t]+\S/.test(line)).length;
}

/**
 * The note request's answer split into its parts, or null when it is not a note. Lenient on purpose, since
 * agent-service's GrowthNoteCheck is the strict reader: a block is either absent or opened and closed by
 * exactly one tag line each, no block may hold another one's tag line, and their order is not checked.
 * Only `short-term` and `long-term` are required, and each must hold exactly `items` headings: the note holds
 * as many examples as the save chose and one goal for each (spark#1218, "Reihenfolge im Notizbuch"), so an
 * answer cut short after the first one, or one with an example too many, is not a note and the fallback
 * stays. Text outside the blocks (a preamble, a code fence around everything) is ignored.
 */
export function parseGrowNote(text: string, items: GrowNoteCount = GROW_NOTE_MAX_ITEMS): GrowNoteParts | null {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const ranges: { name: GrowNoteBlock; open: number; close: number }[] = [];
  for (const name of GROW_NOTE_BLOCKS) {
    const opens = lines.flatMap((line, index) => (tagLine(name, false).test(line) ? [index] : []));
    const closes = lines.flatMap((line, index) => (tagLine(name, true).test(line) ? [index] : []));
    if (opens.length === 0 && closes.length === 0) continue;
    if (opens.length !== 1 || closes.length !== 1 || closes[0]! < opens[0]!) return null;
    ranges.push({ name, open: opens[0]!, close: closes[0]! });
  }
  const inside = (at: number, range: { open: number; close: number }) => at > range.open && at < range.close;
  for (const range of ranges) {
    if (ranges.some((other) => other !== range && (inside(other.open, range) || inside(other.close, range)))) return null;
  }
  const raw = new Map(ranges.map((range) => [range.name, lines.slice(range.open + 1, range.close).join("\n")]));
  const shortTerm = raw.get("short-term");
  const longTerm = raw.get("long-term");
  if (shortTerm === undefined || longTerm === undefined) return null;
  if (headingCount(shortTerm) !== items || headingCount(longTerm) !== items) return null;
  const optional = (name: GrowNoteBlock) => {
    const block = raw.get(name);
    return block === undefined ? null : clean(block) || null;
  };
  return {
    referencePoints: optional("reference-points"),
    growthPattern: optional("growth-pattern"),
    shortTerm: clean(shortTerm),
    longTerm: clean(longTerm),
    start: optional("start"),
    sources: sourceTitles(raw.get("sources") ?? ""),
  };
}

/**
 * The answer as it streams in, for showing only: the block tags go wherever they stand, a tag still being
 * written at the end goes too, and so do the figure markers.
 */
export function growNotePreview(text: string): string {
  return withoutMarkers(text.replace(ANY_TAG, "").replace(/<\/?[a-z-]*$/i, ""))
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// A horizontal rule: a line of three or more dashes, stars or underscores, and nothing else. The same rule
// as GrowthAnswerCheck's RULE_LINE in agent-service.
export const RULE_LINE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;

/**
 * `text` without the paragraph that reads exactly `line`; unchanged when there is none. The rule that set
 * the line off goes with it when nothing stands on its other side any more, so the text neither ends on a
 * rule nor carries two in a row.
 */
export function withoutLine(text: string, line: string): string {
  const paragraphs = text.split(/\n[ \t]*\n/);
  const at = paragraphs.findIndex((paragraph) => paragraph.trim() === line.trim());
  if (at < 0) return text;
  const kept = paragraphs.filter((_, index) => index !== at);
  const isRule = (index: number) => index >= 0 && index < kept.length && RULE_LINE.test(kept[index]!);
  const before = isRule(at - 1) && (at === kept.length || isRule(at));
  const after = !before && isRule(at) && at === 0;
  const stray = before ? at - 1 : after ? at : -1;
  return kept.filter((_, index) => index !== stray).join("\n\n");
}
